import CommonLayout from "../CommonLayout";
import React, { useEffect, useState } from "react";
import Label from "../../../components/Label/Label";
import Input from "../../../shared/Input/Input";
import { useForm } from "react-hook-form";
import ButtonPrimary from "../../../shared/Button/ButtonPrimary";
import { AccountProviderForm } from "./AccountProviderForm";
import { AccountLocationForm } from "./AccountLocationForm";
import { useProviders } from "../../../hooks/collections/useProviders";
import { useLocations } from "hooks/collections/useLocations";
import { useServices } from "../../../hooks/collections/useServices";
import { useAlert } from "../../../@shared/hooks/useAlert";
import { useLoader } from "../../../@shared/hooks/useLoader";
import { useNavigate } from "react-router-dom";
import { useQueryParams } from "../../../@shared/hooks/useQueryParams";
import { IService } from "../../../@core/interfaces/IService";
import ButtonSecondary from "../../../shared/Button/ButtonSecondary";
import { TrashIcon } from "@heroicons/react/16/solid";
import Editor from "../../../@core/components/Editor";
import NcInputNumber from "../../../components/NcInputNumber/NcInputNumber";

interface Props {

}

export const AccountServiceForm = ({}: Props) => {
  const [currentStep, setCurrentStep] = useState(1);
  const { register, handleSubmit, setValue, formState: { errors }, watch, getValues } = useForm({
    defaultValues: {
      title: "",
      description: "",
      price: 0,
      images: [],
      provider: "",
      duration: 0,
      location: ""
    }
  });
  const serviceId = useQueryParams('id');
  const { providers } = useProviders(true);
  const { locations } = useLocations(true);
  const { createService, getService, editService, deleteService } = useServices();
  const alert = useAlert();
  const loader = useLoader();
  const navigate = useNavigate();


  const imagesWatch = watch("images");

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 3));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const submit = async (data) => {
    if (!await alert.confirm('Are you sure you want to save?')) return;

    try {
      loader.start();
      serviceId ? await editService(serviceId.toString(), data) : await createService(data);
      navigate('/account-services');
      alert.toastSuccess('Action completed successfully');
    } catch (e) {
      console.error(e);
      alert.error('Error creating service');
    } finally {
      loader.stop();
    }
  };

  const handleDelete = async () => {
    if (!await alert.confirm('Are you sure you want to delete?')) return;

    try {
      loader.start();
      await deleteService(serviceId as string);
      navigate('/account-services');
    } catch (e) {
      console.log(e);
      alert.error('Error deleting service');
    } finally {
      loader.stop();
    }
  };

  useEffect(() => {
    if (serviceId) {
      (async () => {
        try {
          setCurrentStep(1);
          const service = await getService(serviceId as string) as IService;
          setValue('title', service.title);
          setValue('description', service.description);
          setValue('price', service.price);
          setValue('duration', service.details.duration);
          setValue('provider', service.details.provider.id);
          setValue('location', service.details.location.id);
          setValue('images', service.images);

        } catch (e) {
          console.error(e);
          alert.error('Error', 'Service not found');
          navigate('/account-services');
        }
      })();
    }
  }, []);

  return (
    <div>
      <CommonLayout>
        <form onSubmit={handleSubmit(submit)}>
          <div className={'w-full lg:w-[600px] mx-auto flex flex-col gap-4'}>
            <div className="flex justify-between mb-4">
              <div
                className={`flex-1 text-center py-2 ${currentStep >= 1 ? 'bg-primary-500 text-white dark:bg-primary-700' : 'bg-gray-200 dark:bg-gray-700'}`}
              >
                Basic Info
              </div>
              <div
                className={`flex-1 text-center py-2 ${currentStep >= 2 ? 'bg-primary-500 text-white dark:bg-primary-700' : 'bg-gray-200 dark:bg-gray-700'}`}
              >
                Provider
              </div>
              <div
                className={`flex-1 text-center py-2 ${currentStep >= 3 ? 'bg-primary-500 text-white dark:bg-primary-700' : 'bg-gray-200 dark:bg-gray-700'}`}
              >
                Location
              </div>
            </div>

            {currentStep === 1 && (
              <>
                <h2 className="text-3xl font-semibold">Basic Service Information</h2>
                <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

                <div>
                  <Label>Title</Label>
                  <Input className="mt-1.5" {...register("title", { required: true })}/>
                  {errors.title && <span className="text-red-500">This field is required</span>}
                </div>
                <div>
                  <Label>Description</Label>
                  <div className="mt-1.5">
                    <Editor
                      defaultValue={getValues("description")}
                      onChange={(content: string) => setValue("description", content)}
                    />
                  </div>
                  {errors.description && <span className="text-red-500">This field is required</span>}
                </div>
                <div>
                  <Label>Price</Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">$</span>
                    </div>
                    <Input
                      className="!pl-8 !pr-10"
                      placeholder="0.00" {...register('price', { required: true })}
                      type="number"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">USD</span>
                    </div>
                  </div>
                  {errors.price && <span className="text-red-500">This field is required</span>}
                </div>
                <div>
                  <Label>Duration (hours)</Label>
                  <div className="mt-1.5">
                    <NcInputNumber
                      defaultValue={watch("duration") || 0}
                      min={0}
                      max={24}
                      onChange={(value) => setValue("duration", value)}
                      className="w-full"
                      desc="Use the +/- buttons to adjust the duration"
                    />
                  </div>
                  {errors.duration && <span className="text-red-500">This field is required</span>}
                </div>

                <div>
                  <Label>Images</Label>
                  <div className="dropzone mt-2 border border-dashed p-4 text-center relative">
                    <input
                      type="file"
                      accept={"image/*"}
                      multiple
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      {...register("images")}
                    />
                    <p>Drop files here or click to upload</p>
                  </div>

                  {imagesWatch && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {Array.from(imagesWatch).map((image, index) =>
                        typeof image === 'string' ? (
                          <img
                            key={index}
                            src={image}
                            alt="Preview"
                            className="size-32 object-cover"
                          />
                        ) : (
                          <img
                            key={index}
                            src={URL.createObjectURL(image)}
                            alt="Preview"
                            className="size-32 object-cover"
                          />
                        )
                      )}
                    </div>
                  )}
                </div>

                <div className="flex justify-end">
                  <ButtonPrimary type="button" onClick={nextStep}>Next</ButtonPrimary>
                </div>
              </>
            )}

            {currentStep === 2 && (
              <>
                <h2 className="text-3xl font-semibold">Choose a Provider</h2>
                <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

                <div>
                  <div>
                    <Label>Provider</Label>
                  </div>
                  <select
                    className={`nc-Select h-11 mt-1.5 block w-full text-sm rounded-2xl border-neutral-200 focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50 bg-white dark:border-neutral-700 dark:focus:ring-primary-6000 dark:focus:ring-opacity-25 dark:bg-neutral-900`}
                    {...register("provider", { required: true })}
                    defaultValue={''}
                  >
                    <option value="" disabled>
                      Select a provider
                    </option>
                    {providers.map((provider) => (
                      <option
                        key={provider.id}
                        value={provider.id}
                      >
                        {provider.name}
                      </option>
                    ))}
                  </select>
                  {errors.provider && <span className="text-red-500">Please select a provider</span>}
                </div>

                <div className="flex justify-between">
                  <ButtonSecondary type="button" onClick={prevStep}>Previous</ButtonSecondary>
                  <ButtonPrimary type="button" onClick={nextStep}>Next</ButtonPrimary>
                </div>

                <div className="mt-20">
                  <div className="relative">
                  <span className="text-md bg-white text-gray-500 dark:bg-neutral-900 dark:text-neutral-400">
                    Didn't find the provider you were looking for? Create a new one below
                  </span>
                  </div>
                  <div className="mt-6 py-4 px-6 bg-gray-100 rounded-lg shadow-sm dark:bg-neutral-800">
                    <div className="text-xl font-semibold text-gray-800 dark:text-white">
                      Create a New Provider
                    </div>
                    <div className="w-full border-b border-gray-300 dark:border-neutral-700 my-4"></div>
                    <AccountProviderForm/>
                  </div>
                </div>
              </>
            )}

            {currentStep === 3 && (
              <>
                <h2 className="text-3xl font-semibold">Choose a Location</h2>
                <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

                <div>
                  <div>
                    <Label>Location</Label>
                  </div>
                  <select
                    className={`nc-Select h-11 mt-1.5 block w-full text-sm rounded-2xl border-neutral-200 focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50 bg-white dark:border-neutral-700 dark:focus:ring-primary-6000 dark:focus:ring-opacity-25 dark:bg-neutral-900`}
                    {...register("location", { required: true })}
                    defaultValue={''}
                  >
                    <option value="" disabled>
                      Select a location
                    </option>
                    {locations.map((location) => (
                      <option
                        key={location.id}
                        value={location.id}
                      >
                        {location.name}
                      </option>
                    ))}
                  </select>
                  {errors.location && <span className="text-red-500">Please select a location</span>}
                </div>

                <div className="flex justify-between">
                  <ButtonSecondary type="button" onClick={prevStep}>Previous</ButtonSecondary>
                  <ButtonPrimary type="submit">{serviceId ? 'Update Service' : 'Create Service'}</ButtonPrimary>
                </div>

                <div className="mt-20">
                  <div className="relative">
                  <span className="text-md bg-white text-gray-500 dark:bg-neutral-900 dark:text-neutral-400">
                    Didn't find the location you were looking for? Create a new one below
                  </span>
                  </div>
                  <div className="mt-6 py-4 px-6 bg-gray-100 rounded-lg shadow-sm dark:bg-neutral-800">
                    <div className="text-xl font-semibold text-gray-800 dark:text-white">
                      Create a New Location
                    </div>
                    <div className="w-full border-b border-gray-300 dark:border-neutral-700 my-4"></div>
                    <AccountLocationForm/>
                  </div>
                </div>
              </>
            )}
            {serviceId && <ButtonSecondary className={"mt-10 flex items-center justify-center gap-1"} type={"button"} onClick={handleDelete}><TrashIcon className={"size-3.5"}/>Delete Service</ButtonSecondary>}
          </div>
        </form>
      </CommonLayout>
    </div>
  );
};
